import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { EditCategoryForm } from '@modules/common/components/SelectablePredictionLabel/EditCategoryForm';
import { RemovePredictionModalContent } from '@modules/common/components/SelectablePredictionLabel/RemovePredictionModalContent';
import { Category } from '@modules/common/types';
import { useUpdateEditedPredictions } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackTaxonomyEvent } from '@modules/taxonomy/helpers/trackTaxonomyEvents';
import {
  CategoryRounded,
  EditRounded,
  MergeTypeRounded,
} from '@mui/icons-material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { MenuItem, MenuList, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Feedback, FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';

interface SelectablePredictionLabelMenuProps {
  label: ReasonOrEntityLabelType;
  feedback: Feedback;
  category: Category;
  isEditCategoryMode: boolean;
  setIsEditCategoryMode: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  onClickEditPrediction: () => void;
  onClickMergeWithAnotherReason: () => void;
}

export const SelectablePredictionLabelMenu = ({
  label,
  feedback,
  category,
  isEditCategoryMode,
  setIsEditCategoryMode,
  onClickEditPrediction,
  onClickMergeWithAnotherReason,
  onClose: onClosePopper,
}: SelectablePredictionLabelMenuProps) => {
  const { openModal } = useMuiModalContext();
  const [updateTrackedKeywordCategory, setUpdateTrackedKeywordCategory] =
    useState(false);

  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );

  const { updateEditedPredictions } = useUpdateEditedPredictions(feedback.id);

  useEffect(() => {
    if (updateTrackedKeywordCategory) {
      setUpdateTrackedKeywordCategory(false);
      updateEditedPredictions();
    }
  }, [updateTrackedKeywordCategory, updateEditedPredictions]);

  const onRemovePrediction = () => {
    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: feedback.id,
        editPredictions: recordInEditMode?.editPredictions ?? [],
      },
    });
    openModal({
      contentComponent: () => (
        <RemovePredictionModalContent
          label={label}
          feedbackID={feedback.id}
          category={category}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  if (isEditCategoryMode) {
    return (
      <EditCategoryForm
        label={label}
        feedback={feedback}
        category={category}
        onClose={onClosePopper}
      />
    );
  }

  return (
    <Paper>
      <MenuList
        dense
        autoFocusItem
        sx={{
          p: 1,
        }}
      >
        {label.type === FeedbackLabelType.Entity && (
          <MenuItem
            disableGutters
            sx={{
              borderRadius: 1,
              px: 1,
            }}
            onClick={() => setIsEditCategoryMode(true)}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-start"
            >
              <CategoryRounded
                sx={{
                  color: 'text.disabled',
                }}
                fontSize="small"
              />
              <Typography>Change Category</Typography>
            </Stack>
          </MenuItem>
        )}
        {label.type === FeedbackLabelType.Reason && (
          <MenuItem
            disableGutters
            sx={{
              borderRadius: 1,
              px: 1,
            }}
            onClick={() => {
              onClickEditPrediction();
              trackTaxonomyEvent({
                eventName: 'Edit Reason Clicked',
                from: 'Feedback Card',
                displayName: label.displayName,
                id: label.id,
              });
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-start"
            >
              <EditRounded
                sx={{
                  color: 'text.disabled',
                }}
                fontSize="small"
              />
              <Typography>Edit Reason</Typography>
            </Stack>
          </MenuItem>
        )}
        {label.type === FeedbackLabelType.Reason && (
          <MenuItem
            disableGutters
            sx={{
              borderRadius: 1,
              px: 1,
            }}
            onClick={onClickMergeWithAnotherReason}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-start"
            >
              <MergeTypeRounded
                sx={{
                  color: 'text.disabled',
                }}
                fontSize="small"
              />
              <Typography>Merge with another Reason</Typography>
            </Stack>
          </MenuItem>
        )}
        <MenuItem
          disableGutters
          sx={{
            borderRadius: 1,
            px: 1,
          }}
          onClick={onRemovePrediction}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-start"
          >
            <DeleteRoundedIcon
              sx={{
                color: 'text.disabled',
              }}
              fontSize="small"
            />
            <Typography>Remove Prediction</Typography>
          </Stack>
        </MenuItem>
      </MenuList>
    </Paper>
  );
};
